import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

type useFetchArgs<T> = {
  api: (args: any[]) => Promise<T>;
  deps: any[];
};

export enum Status {
  IDLE = 'IDLE',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

type State<T> = {
  status: Status;
  data?: T;
  errors?: boolean;
};

interface FetchResponseIDLE<T> extends State<T> {
  isLoading: boolean;
  hasError: boolean;
  refetch: () => Promise<void>;
}

type FetchResponsePending<T> = {
  status: Status;
  data?: T;
  errors?: boolean;
  isLoading: boolean;
  hasError: boolean;
  refetch: () => Promise<void>;
};

type FetchResponseSuccess<T> = {
  status: Status;
  data: T;
  errors?: boolean;
  isLoading: boolean;
  hasError: boolean;
  refetch: () => Promise<void>;
};

type FetchResponseError<T> = {
  status: Status;
  data?: T;
  errors: any;
  isLoading: boolean;
  hasError: boolean;
  refetch: () => Promise<void>;
};

const initialState = {
  status: Status.IDLE,
};

enum ActionTypes {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

type Action = {
  type: ActionTypes;
  payload?: any;
};

function reducer<T>(state: State<T>, action: Action): State<T> {
  switch (action.type) {
    case ActionTypes.PENDING: {
      return {
        ...state,
        status: Status.PENDING,
      };
    }

    case ActionTypes.SUCCESS: {
      return {
        ...state,
        status: Status.SUCCESS,
        data: action.payload,
      };
    }

    case ActionTypes.ERROR: {
      return {
        ...state,
        status: Status.ERROR,
        errors: action.payload,
      };
    }
  }
}

const loadingStatuses = [Status.IDLE, Status.PENDING];

export const useFetch = <T>({
  api,
  deps,
}: useFetchArgs<T>):
  | FetchResponseIDLE<T>
  | FetchResponsePending<T>
  | FetchResponseSuccess<T>
  | FetchResponseError<T> => {
  const [state, dispatch] = useReducer<React.Reducer<State<T>, Action>>(reducer, initialState);
  const isCancelled = useRef<boolean>(false);

  const { status, data, errors } = state;

  const { isLoading, hasError } = useMemo(() => {
    return {
      isLoading: loadingStatuses.includes(status),
      hasError: status === Status.ERROR,
    };
  }, [status]);

  const fetchData = useCallback(
    async (args) => {
      dispatch({
        type: ActionTypes.PENDING,
      });

      try {
        const res = await api(args);
        if (!isCancelled.current) {
          dispatch({
            type: ActionTypes.SUCCESS,
            payload: res,
          });
        }
      } catch (e) {
        if (!isCancelled.current) {
          dispatch({
            type: ActionTypes.ERROR,
            payload: e,
          });
        }
      }
    },
    [api]
  );

  useEffect(() => {
    isCancelled.current = false;
    fetchData(deps);

    return () => {
      isCancelled.current = true;
    };
  }, deps);

  const refetch = () => fetchData(deps);

  if (hasError) {
    return { status, errors, isLoading, hasError, refetch };
  }

  if (isLoading) {
    return { status, isLoading, hasError, refetch };
  }

  return { status, data, errors, isLoading, hasError, refetch };
};
